/**
 * EDC → CTMS token handoff.
 *
 * Wave EDC's app launcher opens CTMS with `?token=<EDC JWT>` (or posts a
 * WAVE_TOKEN message into the iframe when CTMS is embedded). That token is
 * NOT a CTMS session — it is a one-shot credential we exchange at
 * `POST /auth/sso/edc` for a native CTMS access + refresh token pair.
 *
 * The token supplied by EDC is ALWAYS the source of truth: when it names a
 * different user than the session already living in this browser, every trace
 * of the previous user — refresh token, remembered email, React Query cache —
 * is purged before the new session is established.
 *
 * Two entry points:
 *   - `bootstrapEdcUrlTokenSync()` runs synchronously in main.tsx BEFORE the
 *     first render, so the token leaves the address bar before any component
 *     mounts and no route guard ever sees a half-established session. This is
 *     the path a full-page handoff takes.
 *   - `installEdcPostMessageBridge()` handles tokens that arrive while the SPA
 *     is already mounted (iframe embedding, EDC-side user switch).
 *
 * Mirrors the Intelligence Hub's auth/edcTokenBridge.ts, with one deliberate
 * difference: the Hub stores the EDC token and sends it as its own bearer
 * (it shares EDC's signing secret), whereas CTMS exchanges it and keeps its
 * own. The EDC token is never persisted here.
 */

// ─── Storage keys owned by the authenticated session ────────────────────────

/**
 * Credentials for the live session. Wiped on logout AND on a user switch.
 * The access token is deliberately absent: it lives in memory only
 * (see api/client.ts).
 */
const SESSION_KEYS = ['ctms_refresh_token'] as const;

/**
 * Convenience state tied to *who* was signed in. Survives an ordinary logout —
 * "remember me" exists precisely so the next login pre-fills — but must not
 * survive a switch to a different user, where it would leak one person's
 * email into another's login form.
 */
const IDENTITY_KEYS = ['ctms_remembered_email'] as const;

/** Fired after a purge so in-memory state above the auth tree can reset. */
export const USER_PURGED_EVENT = 'ctms:user-purged';

/** Set by main.tsx so a purge can drop cached query data too. */
let queryCacheResetter: (() => void) | null = null;

export function registerQueryCacheResetter(fn: (() => void) | null): void {
  queryCacheResetter = fn;
}

// ─── Pending handoff token ──────────────────────────────────────────────────

/**
 * The EDC token captured from the URL before first render, held in memory
 * only — never sessionStorage/localStorage. AuthContext consumes it exactly
 * once during init; `consumePendingEdcToken()` clears it so a remount cannot
 * replay an already-exchanged credential.
 */
let pendingEdcToken: string | null = null;

export function consumePendingEdcToken(): string | null {
  const token = pendingEdcToken;
  pendingEdcToken = null;
  return token;
}

export function hasPendingEdcToken(): boolean {
  return pendingEdcToken !== null;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

export interface DecodedEdcToken {
  token: string;
  userId: string;
  email: string;
}

/** Token representation safe for logs — never log a full JWT. */
export function maskToken(token: string | null | undefined): string {
  if (!token) return '(none)';
  if (token.length <= 16) return '***';
  return `${token.slice(0, 10)}…${token.slice(-6)}`;
}

/**
 * Decode + sanity-check an EDC-issued JWT (shape, required claims, expiry).
 *
 * This is a cheap pre-flight ONLY — it decides whether a round trip to the
 * server is worth making and which user the token names. Signature
 * verification is the backend's job and happens on every exchange; nothing
 * here grants access.
 */
export function decodeEdcToken(jwt: string): DecodedEdcToken | null {
  try {
    const token = jwt.trim();
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')),
    );
    if (!payload.sub || !payload.email) return null;
    if (payload.exp && payload.exp < Math.floor(Date.now() / 1000)) return null;
    return {
      token,
      userId: String(payload.sub),
      email: String(payload.email).toLowerCase(),
    };
  } catch {
    return null;
  }
}

/** Read the user id of the session currently persisted in this browser. */
function readStoredUserId(): string | null {
  try {
    return localStorage.getItem('ctms_session_user_id');
  } catch {
    return null;
  }
}

/**
 * Remember which user the persisted refresh token belongs to, so a later
 * handoff can tell "same user, new token" from "different user, purge first".
 */
export function rememberSessionUserId(userId: string | null): void {
  try {
    if (userId) localStorage.setItem('ctms_session_user_id', userId);
    else localStorage.removeItem('ctms_session_user_id');
  } catch {
    /* storage unavailable */
  }
}

/**
 * Remove user-scoped state: persisted credentials and the React Query cache.
 * Called on logout, on 401/expiry, and before applying a different user's
 * token.
 *
 * Pass `identityChanged` when a DIFFERENT person is taking over this browser.
 * That additionally drops identity-tied conveniences (the remembered email),
 * which an ordinary logout deliberately keeps.
 */
export function purgeUserScopedState(
  reason: string,
  { identityChanged = false }: { identityChanged?: boolean } = {},
): void {
  const keys = identityChanged
    ? [...SESSION_KEYS, ...IDENTITY_KEYS]
    : [...SESSION_KEYS];
  for (const key of keys) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* storage unavailable — nothing to purge */
    }
  }
  rememberSessionUserId(null);
  queryCacheResetter?.();
  try {
    window.dispatchEvent(new Event(USER_PURGED_EVENT));
  } catch {
    /* non-browser environment (tests) */
  }
  console.info('[auth] purged user-scoped state', { reason, identityChanged });
}

/** Strip only the `token` param from the URL, preserving all other params. */
export function stripTokenParam(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('token')) return;
    url.searchParams.delete('token');
    window.history.replaceState(
      {},
      '',
      url.pathname + url.search + url.hash,
    );
  } catch {
    /* ignore */
  }
}

// ─── Entry point 1: pre-render URL capture ──────────────────────────────────

/**
 * Consume a `?token=` handoff BEFORE the first render. Must be called
 * synchronously from main.tsx, ahead of root.render(), so the credential
 * leaves the address bar before any component mounts.
 *
 * Rules:
 *  - no token param             → no-op (a plain reload keeps the session)
 *  - token for the SAME user    → queue the exchange, keep existing state
 *  - token for a DIFFERENT user → purge everything, then queue the exchange
 *  - invalid/expired token      → purge any existing session and require
 *    login; silently keeping the previous user here is exactly the stale-user
 *    bug the Intelligence Hub documents.
 */
export function bootstrapEdcUrlTokenSync(): void {
  let urlToken: string | null = null;
  try {
    urlToken = new URLSearchParams(window.location.search).get('token');
  } catch {
    return;
  }
  if (!urlToken) return;

  // Strip first, unconditionally: even a token we go on to reject must not
  // survive in the address bar, browser history, or an outbound Referer.
  stripTokenParam();

  const decoded = decodeEdcToken(urlToken);
  const previousUserId = readStoredUserId();

  if (!decoded) {
    if (previousUserId) {
      // Identity unknown — we cannot prove the incoming token names the same
      // person, so treat it as a switch and drop identity-tied state too.
      purgeUserScopedState('invalid/expired EDC token while a session existed', {
        identityChanged: true,
      });
    }
    console.warn('[auth-bridge] EDC token is invalid or expired — login required');
    return;
  }

  const isUserSwitch = !!previousUserId && previousUserId !== decoded.userId;
  if (isUserSwitch) {
    purgeUserScopedState(
      `EDC token user switch (${previousUserId} → ${decoded.userId})`,
      { identityChanged: true },
    );
  }

  pendingEdcToken = decoded.token;

  console.info('[auth-bridge] EDC token captured before first render', {
    token: maskToken(decoded.token),
    user: decoded.email,
    userSwitch: isUserSwitch,
  });
}

// ─── Entry point 2: postMessage / late-arriving tokens ──────────────────────

/**
 * Handle tokens that arrive after the SPA has mounted:
 *   - `postMessage({ type: 'WAVE_TOKEN', token })` — EDC sends this after an
 *     embedded iframe loads, and again when the EDC-side user changes.
 *   - an SPA-internal navigation that adds `?token=`.
 *
 * `onToken` receives a token that has already passed the client-side shape
 * check; it is expected to run the server exchange.
 *
 * SECURITY: `allowedOrigins` gates which windows may hand us a credential.
 * Without it any page that can get a handle on this window could inject a
 * token. Pass the EDC origin(s); an empty list rejects every message.
 */
export function installEdcPostMessageBridge(
  onToken: (token: string, source: 'url' | 'postMessage') => void,
  allowedOrigins: readonly string[],
): () => void {
  const applyToken = (raw: string, source: 'url' | 'postMessage') => {
    const decoded = decodeEdcToken(raw);
    if (!decoded) {
      console.warn(`[auth-bridge] ignoring invalid/expired token from ${source}`);
      return;
    }
    const previousUserId = readStoredUserId();
    if (previousUserId && previousUserId !== decoded.userId) {
      purgeUserScopedState(
        `${source} token user switch (${previousUserId} → ${decoded.userId})`,
        { identityChanged: true },
      );
    }
    console.info(`[auth-bridge] applying token from ${source}`, {
      token: maskToken(decoded.token),
      user: decoded.email,
    });
    onToken(decoded.token, source);
  };

  // Safety net for SPA-internal navigations that add ?token= after mount.
  // The pre-render bootstrap normally consumes it first.
  try {
    const urlToken = new URLSearchParams(window.location.search).get('token');
    if (urlToken) {
      stripTokenParam();
      applyToken(urlToken, 'url');
    }
  } catch {
    /* ignore */
  }

  const handler = (event: MessageEvent) => {
    if (!allowedOrigins.includes(event.origin)) return;
    if (
      event.data?.type === 'WAVE_TOKEN' &&
      typeof event.data.token === 'string'
    ) {
      applyToken(event.data.token, 'postMessage');
    }
  };
  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}
